import React, { createContext, useContext, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Container, Box } from '@mui/material';
import './styles/ClickableAddress.css';
import { AuthProvider } from './contexts/AuthContext';
import { CompanyProvider } from './contexts/CompanyContext';
import PrivateRoute from './components/PrivateRoute';
import HomePage from './pages/HomePage';
import ResultsPage from './pages/ResultsPage';
import LoginPage from './pages/LoginPage';
import SearchHistoryPage from './pages/SearchHistoryPage';
import UserGuidePage from './pages/UserGuidePage';
import Header from './components/Header';
import CompanyList from './components/CompanyList';
import CompanyDetail from './components/CompanyDetail';
import ErrorNotification from './components/ErrorNotification';
import SessionExpiredDialog from './components/SessionExpiredDialog';
import NotFound from './components/NotFound';

// Глобальный контекст для уведомлений
const GlobalNotificationContext = createContext();

export const useGlobalNotification = () => {
  const context = useContext(GlobalNotificationContext);
  if (!context) {
    throw new Error('useGlobalNotification must be used within GlobalNotificationProvider');
  }
  return context;
};

const GlobalNotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState({ open: false, error: null, severity: 'error' });
  const [isSearching, setIsSearching] = useState(false);

  const showNotification = (error, severity = 'auto') => {
    setNotification({
      open: true,
      error: error,
      severity: severity
    });
  };

  const showSuccess = (message) => {
    const successError = {
      response: {
        data: {
          result: "SUCCESS",
          message: message
        }
      }
    };
    showNotification(successError, 'success');
  };

  const showWarning = (message) => {
    const warningError = {
      response: {
        data: {
          message: message
        }
      }
    };
    showNotification(warningError, 'warning');
  };

  const showError = (message) => {
    const errorObj = {
      response: {
        data: {
          detail: message
        }
      }
    };
    showNotification(errorObj, 'error');
  };

  const hideNotification = () => {
    setNotification({ open: false, error: null, severity: 'error' });
  };

  const startSearch = () => {
    setIsSearching(true);
  };

  const endSearch = () => {
    setIsSearching(false);
  };

  const value = {
    showNotification,
    showSuccess,
    showWarning,
    showError,
    hideNotification,
    isSearching,
    startSearch,
    endSearch
  };

  return (
    <GlobalNotificationContext.Provider value={value}>
      {children}
      <ErrorNotification
        error={notification.error}
        open={notification.open}
        onClose={hideNotification}
        severity={notification.severity}
      />
    </GlobalNotificationContext.Provider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CompanyProvider>
          <GlobalNotificationProvider>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <Header />
              <SessionExpiredDialog />
              <Container component="main" sx={{ mt: 4, mb: 4, flex: 1 }}>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/" element={
                    <PrivateRoute>
                      <HomePage />
                    </PrivateRoute>
                  } />
                  <Route path="/search-history" element={
                    <PrivateRoute>
                      <SearchHistoryPage />
                    </PrivateRoute>
                  } />
                  <Route path="/guide" element={
                    <PrivateRoute>
                      <UserGuidePage />
                    </PrivateRoute>
                  } />
                  <Route path="/companies" element={
                    <PrivateRoute>
                      <CompanyList />
                    </PrivateRoute>
                  } />
                  <Route path="/company/:id" element={
                    <PrivateRoute>
                      <CompanyDetail />
                    </PrivateRoute>
                  } />
                  <Route path="/results/:taskId" element={
                    <PrivateRoute>
                      <ResultsPage />
                    </PrivateRoute>
                  } />
                  {/* Catch-all route для несуществующих страниц */}
                  <Route path="*" element={
                    <PrivateRoute>
                      <NotFound />
                    </PrivateRoute>
                  } />
                </Routes>
              </Container>
              <Box component="footer" sx={{ py: 3, px: 2, mt: 'auto', backgroundColor: (theme) => theme.palette.grey[100] }}>
                <Container maxWidth="sm">
                  <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                    Система Анализа Компаний © {new Date().getFullYear()}
                  </Box>
                </Container>
              </Box>
            </Box>
          </GlobalNotificationProvider>
        </CompanyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
